import { Event, SDK } from '@commercetools/frontend-sdk';
import { PaginatedResult } from 'shared/types/result';
import { Review } from 'shared/types/review/Review';
import { CreateReviewAction, QueryReviewsAction } from '../../types/actions/ReviewActions';
import { CreateReviewPayload, QueryReviewsPayload } from '../../types/payloads/ReviewPayloads';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import { OrderBy, SortBy } from 'shared/types/query/ReviewQuery';

export type ReviewActions = {
  create: CreateReviewAction;
  query: QueryReviewsAction;
};

export const getReviewActions = (sdk: SDK<ComposableCommerceEvents>): ReviewActions => ({
  create: async (
    payload: CreateReviewPayload,
    options: {
      parallel?: boolean;
      customHeaderValue?: string;
      serverOptions?: object;
    } = {},
  ) => {
    // Map payload to match the backend expectation
    const mappedPayload: CreateReviewPayload = {
      productId: payload.productId,
      title: payload.title,
      description: payload.description, // Backend expects 'description', not 'text'
      rating: payload.rating,
      authorName: payload.authorName,
    };

    const response = await sdk.callAction<Review>({
      actionName: 'review/createReview',
      payload: mappedPayload,
      parallel: options.parallel,
      customHeaderValue: options.customHeaderValue,
      serverOptions: options.serverOptions,
    });

    if (!response.isError) {
      sdk.trigger(
        new Event({
          eventName: 'reviewCreated',
          data: {
            review: response.data,
            productId: payload.productId,
          },
        }),
      );
    }

    return response;
  },

  query: async (
    payload: QueryReviewsPayload,
    options: {
      parallel?: boolean;
      customHeaderValue?: string;
      serverOptions?: object;
    } = {},
  ) => {
    const queryParams: QueryReviewsPayload = {
      productId: payload.productId,
      ...(payload.limit !== undefined && { limit: payload.limit }),
      ...(payload.page !== undefined && { page: payload.page }),
      ...(payload.cursor && { cursor: payload.cursor }),
    };

    // Determine sorting parameters
    if (payload.sortBy && payload.orderBy) {
      queryParams.sortBy = payload.sortBy;
      queryParams.orderBy = payload.orderBy;
    } else if (payload.sortAttributes) {
      const [key, val] = Object.entries(payload.sortAttributes)[0] as [SortBy, OrderBy];
      queryParams.sortBy = key;
      queryParams.orderBy = val;
    } else {
      queryParams.sortBy = 'createdAt';
      queryParams.orderBy = 'desc';
    }

    const response = await sdk.callAction<PaginatedResult<Review>>({
      actionName: 'review/query',
      query: queryParams,
      parallel: options.parallel,
      customHeaderValue: options.customHeaderValue,
      serverOptions: options.serverOptions,
    });

    if (!response.isError) {
      sdk.trigger(
        new Event({
          eventName: 'reviewsFetched',
          data: {
            reviews: response.data,
            productId: payload.productId,
          },
        }),
      );
    }

    return response;
  },
});
